import { Injectable } from '@angular/core';
import type { Locale } from 'date-fns/locale';
import * as z from 'zod';

import { getDateFnsLocale } from '../../../shared/utils/get-date-fns-locale.util';
import type { IFallbackDateFormats, ILanguageFormats, ILocalizationSettings } from '../../../user/models/responses';
import type { User } from '../../../user/models/user';
import { DateFormats, TimeFormats } from '../../../user/models/user';
import { DEFAULT_LOCALE, DEFAULT_USER_LOCALE, LocalStorageItem } from '../../constants';
import { asLocale, LOCALE } from '../../enums/locale.enum';

// Everything this service reads off the user. The three formats are optional here because the
// pre-login fallback has none, and plain strings because a value an older release stored need not
// match today's `DateFormatEnum` — every read already falls back on a blank one.
type DateTimeUser = Pick<User, 'locale'> & {
  date_format?: string | undefined;
  time_format?: string | undefined;
  datetime_format?: string | undefined;
};

const storedDateTimeUserSchema = z.looseObject({
  locale: z.enum(LOCALE),
  date_format: z.string().optional(),
  time_format: z.string().optional(),
  datetime_format: z.string().optional(),
});

@Injectable({ providedIn: 'root' })
export class DateTimeService {
  user!: DateTimeUser;

  #dateFormat: string = '';
  #timeFormat: string = '';
  #dateTimeFormat: string = '';

  #userLocale: string = '';
  #locale: Locale = DEFAULT_LOCALE;
  #userLocaleFormats: ILanguageFormats | null = null;

  constructor() {
    this.setUser();
    this.setLocale();
    this.updateFormats(this.user);
  }

  get dateFormat(): string {
    return this.#dateFormat;
  }

  get timeFormat(): string {
    return this.#timeFormat;
  }

  get dateTimeFormat(): string {
    return this.#dateTimeFormat;
  }

  set userLocale(value: string) {
    this.#userLocale = value;
  }

  set locale(locale: Locale) {
    this.#locale = locale;
  }

  get locale(): Locale {
    return this.#locale;
  }

  // The stored user is unvalidated JSON, so its locale is re-checked rather than trusted.
  get #lang(): LOCALE {
    return asLocale(this.getLanguage()) ?? DEFAULT_USER_LOCALE;
  }

  /** Until `/localisation-settings` answers, date-fns' own CLDR data for the locale stands in. */
  get fallbackFormats(): IFallbackDateFormats {
    const served = this.#userLocaleFormats;
    if (served) {
      return { date: served.date[this.#lang], time: served.time[this.#lang], datetime: served.datetime[this.#lang] };
    }
    const { formatLong } = getDateFnsLocale(this.#lang);
    // A lone `y` is a two-digit year to Excel, and `xlsx.service` feeds these to `numberFormat`.
    const date = formatLong.date({ width: 'short' }).replace(/y+$/, 'yyyy');
    const time = formatLong.time({ width: 'medium' });
    return { date, time, datetime: `${date} ${time}` };
  }

  public getLanguage(): string {
    return this.#userLocale;
  }

  private setUser(): void {
    const currentUser = localStorage.getItem(LocalStorageItem.User);
    // Not via `UtilsService.storage`: it injects this service, so that would be a DI cycle. Nor via
    // the `user` codec, whose whole-shape validation would make a stale entry throw out of a constructor.
    const parsed = storedDateTimeUserSchema.safeParse(currentUser ? JSON.parse(currentUser) : null);
    this.user = parsed.success ? parsed.data : { locale: DEFAULT_USER_LOCALE };
  }

  private setLocale(): void {
    this.#userLocale = this.user.locale;
  }

  public setFormats({ language_formats }: ILocalizationSettings): void {
    this.#userLocaleFormats = language_formats;
    this.updateFormats(this.user);
  }

  public updateFormats(user: DateTimeUser) {
    const fallback = this.fallbackFormats;
    this.#dateFormat = user.date_format || fallback.date;
    this.#timeFormat = user.time_format || fallback.time;
    this.#dateTimeFormat = user.datetime_format || fallback.datetime;
  }

  public getDateFormat(format?: string, time?: 'notime' | 'withtime'): string {
    const fallback = this.fallbackFormats;
    if (!format) {
      return time === 'notime' ? fallback.date : fallback.datetime;
    }
    let result = format;
    if (format.includes(TimeFormats.Local)) {
      result = time === 'notime' ? result.replace(TimeFormats.Local, '').trim() : result.replace(TimeFormats.Local, fallback.time);
    }

    if (format.includes(DateFormats.Local)) {
      result = result.replace(DateFormats.Local, fallback.date);
    }

    return result;
  }
}
